import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiUrl } from '@/lib/utils';

const API = getApiUrl();

// File type icons mapping
const getFileIcon = (contentType) => {
  if (contentType.includes('pdf')) return '📄';
  if (contentType.includes('word') || contentType.includes('document')) return '📝';
  if (contentType.includes('excel') || contentType.includes('spreadsheet')) return '📊';
  if (contentType.includes('image')) return '🖼️';
  if (contentType.includes('text')) return '📃';
  return '📎';
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const AnnouncementsPage = ({ user, t, buildingId = null }) => {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ title: '', content: '' });
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'superadmin' && user?.role !== 'resident') {
      navigate('/app');
      return;
    }
    fetchAnnouncements();
  }, [user, navigate, buildingId]);

  const fetchAnnouncements = async () => {
    try {
      const url = buildingId ? `${API}/announcements?building_id=${buildingId}` : `${API}/announcements`;
      const response = await axios.get(url, { withCredentials: true });
      setAnnouncements(response.data);
    } catch (error) {
      toast.error(t('loadDataFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    
    for (const file of files) {
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(t('fileTooLarge') || `${file.name} is too large. Max 10MB allowed.`);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const url = buildingId 
          ? `${API}/announcements/upload-attachment?building_id=${buildingId}`
          : `${API}/announcements/upload-attachment`;
        
        const response = await axios.post(url, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setAttachments(prev => [...prev, response.data]);
        toast.success(t('fileUploaded') || `${file.name} uploaded successfully`);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(error.response?.data?.detail || t('uploadFailed') || `Failed to upload ${file.name}`);
      }
    }
    
    setUploading(false);
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = async (attachmentId) => {
    try {
      const url = buildingId 
        ? `${API}/announcements/attachment/${attachmentId}?building_id=${buildingId}`
        : `${API}/announcements/attachment/${attachmentId}`;
      
      await axios.delete(url, { withCredentials: true });
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
      toast.success(t('attachmentRemoved') || 'Attachment removed');
    } catch (error) {
      toast.error(t('removeFailed') || 'Failed to remove attachment');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const url = buildingId ? `${API}/announcements?building_id=${buildingId}` : `${API}/announcements`;
      const payload = {
        title: formData.title,
        content: formData.content,
        attachment_ids: attachments.map(a => a.id)
      };
      
      await axios.post(url, payload, { withCredentials: true });
      toast.success(t('announcementCreated'));
      setShowModal(false);
      setFormData({ title: '', content: '' });
      setAttachments([]);
      fetchAnnouncements();
    } catch (error) {
      toast.error(t('operationFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (announcementId) => {
    if (!window.confirm(t('deleteAnnouncement'))) return;
    try {
      const url = buildingId 
        ? `${API}/announcements/${announcementId}?building_id=${buildingId}`
        : `${API}/announcements/${announcementId}`;
      await axios.delete(url, { withCredentials: true });
      toast.success(t('announcementDeleted'));
      fetchAnnouncements();
    } catch (error) {
      toast.error(t('operationFailed'));
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const url = buildingId 
        ? `${API}/announcements/attachment/${attachment.id}?building_id=${buildingId}`
        : `${API}/announcements/attachment/${attachment.id}`;
      
      const response = await axios.get(url, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: attachment.content_type });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = attachment.original_filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      toast.error(t('downloadFailed') || 'Failed to download file');
    }
  };

  const handleCloseModal = () => {
    // Clean up pending attachments when closing without saving
    attachments.forEach(att => {
      handleRemoveAttachment(att.id).catch(() => {});
    });
    setShowModal(false);
    setFormData({ title: '', content: '' });
    setAttachments([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/app')} className="text-gray-600 hover:text-gray-900">
                ← {t('back')}
              </button>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('announcements')}</h1>
            </div>
            {isAdmin && (
              <button
                data-testid="create-announcement-button"
                onClick={() => setShowModal(true)}
                className="px-4 py-2 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-colors"
              >
                {t('newAnnouncement')}
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto space-y-6">
          {announcements.length === 0 ? (
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg text-center">
              <p className="text-gray-500">{t('noAnnouncements') || 'No announcements yet.'}</p>
            </div>
          ) : (
            announcements.map((announcement) => (
              <div key={announcement.id} className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg" data-testid={`announcement-${announcement.id}`}>
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{announcement.title}</h3>
                  {isAdmin && (
                    <button
                      onClick={() => handleDelete(announcement.id)}
                      className="text-red-600 hover:text-red-700 text-sm"
                      data-testid={`delete-announcement-${announcement.id}`}
                    >
                      {t('delete')}
                    </button>
                  )}
                </div>
                <p className="text-gray-700 mb-4 whitespace-pre-wrap" style={{ fontFamily: 'Inter, sans-serif' }}>{announcement.content}</p>
                
                {/* Attachments Section */}
                {announcement.attachments && announcement.attachments.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-sm font-medium text-gray-600 mb-2">{t('attachments') || 'Ekler'}:</p>
                    <div className="flex flex-wrap gap-2">
                      {announcement.attachments.map((att) => (
                        <button
                          key={att.id}
                          onClick={() => handleDownloadAttachment(att)}
                          className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm"
                          data-testid={`download-attachment-${att.id}`}
                        >
                          <span>{getFileIcon(att.content_type)}</span>
                          <span className="max-w-[150px] truncate">{att.original_filename}</span>
                          <span className="text-gray-400 text-xs">({formatFileSize(att.size)})</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="flex justify-between items-center text-sm text-gray-500 mt-4">
                  <span>{announcement.created_by_name || 'Admin'}</span>
                  <span>{new Date(announcement.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create Announcement Modal */}
      {showModal && isAdmin && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="create-announcement-modal">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('newAnnouncement')}</h2>
            <form onSubmit={handleCreate}>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('title')}</label>
                <input
                  type="text"
                  data-testid="title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('content')}</label>
                <textarea
                  data-testid="content-input"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  rows="4"
                  required
                />
              </div>
              
              {/* File Attachments Section */}
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">{t('attachments') || 'Ekler'}</label>
                
                {/* Upload Button */}
                <div className="mb-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
                    data-testid="file-input"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-colors w-full justify-center"
                    data-testid="upload-file-button"
                  >
                    {uploading ? (
                      <>
                        <span className="animate-spin">⏳</span>
                        <span>{t('uploading') || 'Yükleniyor...'}</span>
                      </>
                    ) : (
                      <>
                        <span>📎</span>
                        <span>{t('addAttachment') || 'Dosya Ekle'}</span>
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    {t('allowedFileTypes') || 'PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, GIF, TXT (max 10MB)'}
                  </p>
                </div>
                
                {/* Uploaded Files List */}
                {attachments.length > 0 && (
                  <div className="space-y-2">
                    {attachments.map((att) => (
                      <div 
                        key={att.id}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                        data-testid={`attachment-${att.id}`}
                      >
                        <div className="flex items-center gap-2 overflow-hidden">
                          <span>{getFileIcon(att.content_type)}</span>
                          <span className="truncate text-sm">{att.original_filename}</span>
                          <span className="text-xs text-gray-400">({formatFileSize(att.size)})</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(att.id)}
                          className="text-red-500 hover:text-red-700 p-1"
                          data-testid={`remove-attachment-${att.id}`}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex gap-4">
                <button 
                  type="button" 
                  onClick={handleCloseModal} 
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={submitting}
                >
                  {t('cancel')}
                </button>
                <button 
                  type="submit" 
                  data-testid="submit-announcement" 
                  className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={submitting || uploading}
                >
                  {submitting ? (t('creating') || 'Oluşturuluyor...') : t('create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementsPage;
